import { useEffect, useState } from "react";
import { useRoute, useLocation } from "wouter";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { useI18n } from "@/lib/i18n";
import { getGoogleSignInUrl } from "@/const";
import { isNative, nativeGoogleSignIn } from "@/native";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Invitation route. Signed out, we send the visitor to Google carrying the
 * invitation, and they come back to this same URL.
 *
 * ── WHY ARRIVING HERE NO LONGER JOINS YOU (A0-P1) ───────────────────────────
 * This page used to call `team.acceptInvite` from an effect on mount. Landing
 * on the route WAS accepting it. Combined with an invite-return cookie that was
 * not bound to a sign-in transaction, an ordinary sign-in could be redirected
 * here and silently joined to a team the person never chose — remotely
 * plantable, because acceptance is bearer-only and not email-bound.
 *
 * The cookie is now transaction-bound, and this is the second, independent
 * guard: arriving only DISPLAYS the invitation. Membership is created when the
 * person presses Accept, and at no other time.
 */
export default function InviteAccept() {
  const { isAuthenticated, loading } = useAuth();
  const [, params] = useRoute<{ token: string }>("/invite/:token");
  const [, navigate] = useLocation();
  const { t, locale } = useI18n();
  const accept = trpc.team.acceptInvite.useMutation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      // Previously `getLoginUrl()`, which is the Manus portal helper and
      // returns "#" in production — the invitee clicked through to nothing.
      // The native shell keeps its own canonical entry (Google blocks OAuth in
      // embedded webviews); it lands on "/" rather than back here, which is the
      // pre-existing native behaviour and is recorded as a known limitation.
      const target = getGoogleSignInUrl(
        params?.token ? { kind: "team", token: params.token } : undefined,
      );
      void isNative().then(native => {
        if (native) void nativeGoogleSignIn();
        else window.location.href = target;
      });
    }
    // Signed in: render the invitation and wait. Nothing is accepted here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isAuthenticated, params?.token]);

  /** The ONLY path that creates a membership. Requires a deliberate press. */
  const onAccept = () => {
    if (!params?.token || accept.isPending) return;
    accept.mutate(
      { token: params.token },
      {
        onSuccess: () => {
          toast.success(locale === "ar" ? "تم الانضمام إلى الفريق" : "You're in the team");
          navigate("/");
        },
        onError: e => {
          setError(e.message);
        },
      },
    );
  };

  // Signed out we are on our way to Google; don't flash the invitation UI.
  if (loading || !isAuthenticated || (!error && accept.isPending)) {
    return (
      <div className="min-h-screen grid place-items-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (error) {
    // `already_used` is no longer raised by the server (invites are
    // multi-use until expiry), but keep the branch as a graceful
    // fallback for any in-flight client that fires the old error code.
    const message =
      error === "expired"
        ? locale === "ar"
          ? "انتهت صلاحية رابط الدعوة."
          : "This invite link has expired."
        : error === "already_used"
          ? locale === "ar"
            ? "انتهت صلاحية رابط الدعوة."
            : "This invite link has expired."
          : locale === "ar"
            ? "رابط الدعوة غير صالح."
            : "This invite link isn't valid.";
    return (
      <div className="min-h-screen grid place-items-center px-6">
        <div className="max-w-sm text-center">
          <div className="font-display text-2xl mb-2">{t.invite}</div>
          <div className="text-sm text-muted-foreground mb-6">{message}</div>
          <button
            onClick={() => navigate("/")}
            className="rounded-full bg-coral text-white px-5 py-2 text-sm font-medium"
          >
            {t.backToCanvas}
          </button>
        </div>
      </div>
    );
  }

  // Signed in, invitation not yet accepted. Displaying is not accepting.
  return (
    <div className="min-h-screen grid place-items-center px-6">
      <div className="max-w-sm text-center">
        <div className="font-display text-2xl mb-2">{t.invite}</div>
        <div className="text-sm text-muted-foreground mb-6">
          {locale === "ar"
            ? "تمت دعوتك للانضمام إلى فريق. لن تنضم حتى تختار القبول."
            : "You've been invited to join a team. You won't join until you accept."}
        </div>
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={onAccept}
            disabled={accept.isPending || !params?.token}
            className="rounded-full bg-coral text-white px-5 py-2 text-sm font-medium disabled:opacity-60"
          >
            {locale === "ar" ? "قبول الدعوة" : "Accept invitation"}
          </button>
          <button
            onClick={() => navigate("/")}
            className="rounded-full border px-5 py-2 text-sm font-medium"
          >
            {t.backToCanvas}
          </button>
        </div>
      </div>
    </div>
  );
}
